import { Code, Flex, SimpleGrid } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { AttributeInput } from 'packages/common-frontend/components/AttributeInput/AttributeInput';
import { Attribute } from 'packages/common-frontend/components/AttributeInput/types';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PageTemplate } from '~/core/routes/dev/PageTemplate';

const ATTRIBUTES: Attribute[] = [
  {
    attributeId: uuidv4(),
    displayName: 'String Field',
    type: 'string',
    value: 'foo',
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: undefined },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Select Field',
    type: 'string',
    value: 'foo',
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: ['foo', 'bar', 'baz'] },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Number Field (float)',
    type: 'float',
    value: 326.24,
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: {},
  },
  {
    attributeId: uuidv4(),
    displayName: 'USD Field (float)',
    type: 'float',
    value: 1234.56,
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: {
      display: {
        displayType: 'currency',
        currencyCode: 'USD',
      },
    },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Number Field (integer)',
    type: 'integer',
    value: 326,
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: undefined },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Percent Field',
    type: 'percentageRate',
    value: 0.42,
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: undefined },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Signed Percent Field (negative example)',
    type: 'percentageRate',
    //  -5% displayed; stored value is -0.05
    value: -0.05,
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: undefined },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Date Field',
    type: 'fullDateStr',
    value: '2025-06-10',
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: undefined },
  },
  {
    attributeId: uuidv4(),
    displayName: 'Boolean Field',
    type: 'boolean',
    value: true,
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: { allowedValues: undefined },
  },
  {
    attributeId: uuidv4(),
    displayName: 'String Array Field',
    type: 'stringArray',
    value: ['Item 2', 'Item 4'],
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: {
      allowedValues: [
        'Item 1',
        'Item 2',
        'Item 3',
        'Item 4',
        'Item 5',
        'Item 6',
      ],
    },
  },
  {
    attributeId: uuidv4(),
    displayName: 'String Array (No Options) Field',
    type: 'stringArray',
    value: ['Item 2', 'Item 4'],
    attributeValueId: uuidv4(),
    effectiveFromReportingDate: '2024-06-01',
    validation: {
      allowedValues: undefined,
    },
  },
];

type FormProps = {
  attributeList: Attribute[];
  updateAttributeList: React.Dispatch<React.SetStateAction<Attribute[]>>;
};

const Form = ({ attributeList, updateAttributeList }: FormProps) => {
  const handleUpdate = useCallback(
    (updatedAttribute: typeof ATTRIBUTES[number]) => {
      updateAttributeList((prev) =>
        prev.map((attr) =>
          attr.attributeId === updatedAttribute.attributeId
            ? updatedAttribute
            : attr,
        ),
      );
    },
    [updateAttributeList],
  );

  return (
    <SimpleGrid
      columns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
      gap="1.5rem"
      flexGrow={1}
    >
      {attributeList.map((attribute) => (
        <AttributeInput
          key={attribute.attributeId}
          attribute={attribute}
          onUpdate={handleUpdate}
          onOpenDrawer={() => {}}
        />
      ))}
    </SimpleGrid>
  );
};

const FormContainer = () => {
  const [attributeList, updateAttributeList] = useState(ATTRIBUTES);

  return (
    <Flex justify="center" flex="1">
      <Flex width="65%" alignSelf="flex-start">
        <Form
          attributeList={attributeList}
          updateAttributeList={updateAttributeList}
        />
      </Flex>

      <Flex
        width="35%"
        mx="2rem"
        direction="column"
        position="sticky"
        top="2rem"
        height="fit-content"
      >
        <Code as="pre" whiteSpace="pre-wrap" width="100%" p="1rem">
          {JSON.stringify(attributeList, null, 2)}
        </Code>
      </Flex>
    </Flex>
  );
};

export const Route = () => (
  <RouteErrorBoundary
    resetAfterCatch={false}
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <div>Uh oh</div>;
    }}
  >
    <PageTemplate title="./common-frontend/AttributeInput">
      <FormContainer />
    </PageTemplate>
  </RouteErrorBoundary>
);
